import React, { useState } from "react";
import { Dialog, IconButton } from "@mui/material";
import {
  Calendar,
  Clock,
  X,
  CheckCircle2,
  Info,
  CalendarDays,
  PlusCircle,
} from "lucide-react";
import { ExplanationRequest } from "../../../types/explanations";

interface AssignSlotsModalProps {
  open: boolean;
  request: ExplanationRequest | any;
  onAssign: (slots: { startTime: Date; endTime: Date }[]) => void;
  onClose: () => void;
}

interface ProposedSlot {
  id: string;
  startTime: Date;
  endTime: Date;
}

const AssignSlotsModal: React.FC<AssignSlotsModalProps> = ({
  open,
  request,
  onAssign,
  onClose,
}) => {
  const [proposedSlots, setProposedSlots] = useState<ProposedSlot[]>([]);
  const [showCustomSlotForm, setShowCustomSlotForm] = useState<boolean>(false);
  const [customDate, setCustomDate] = useState<string>("");
  const [customTime, setCustomTime] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  // Formater la date et l'heure
  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString("fr-FR", {
        weekday: "long",
        day: "numeric",
        month: "long",
      }),
      time: date.toLocaleTimeString("fr-FR", {
        hour: "2-digit",
        minute: "2-digit",
      }),
    };
  };

  // Supprimer un créneau proposé
  const removeProposedSlot = (slotId: string) => {
    setProposedSlots((prev) => prev.filter((slot) => slot.id !== slotId));
  };

  // Réinitialiser le modal
  const resetModal = () => {
    setProposedSlots([]);
    setShowCustomSlotForm(false);
    setCustomDate("");
    setCustomTime("");
  };

  // Ajouter un créneau personnalisé
  const addCustomSlot = () => {
    if (!customDate || !customTime) return;
    if (proposedSlots.length >= 3) {
      alert("Vous devez proposer exactement 3 créneaux");
      return;
    }

    // Créer la date/heure à partir des inputs
    const [hours, minutes] = customTime.split(":");
    const slotDate = new Date(customDate);
    slotDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);

    const endTime = new Date(slotDate);
    endTime.setHours(slotDate.getHours() + 1);

    // Vérifier que la date n'est pas dans le passé
    if (slotDate <= new Date()) {
      alert("Vous ne pouvez pas créer un créneau dans le passé");
      return;
    }

    // Vérifier qu'il n'y a pas de conflit avec les créneaux existants
    const hasConflict = proposedSlots.some((slot) => {
      const slotStart = new Date(slot.startTime);
      const slotEnd = new Date(slot.endTime);
      return (
        (slotDate >= slotStart && slotDate < slotEnd) ||
        (endTime > slotStart && endTime <= slotEnd) ||
        (slotDate <= slotStart && endTime >= slotEnd)
      );
    });

    if (hasConflict) {
      alert("Ce créneau entre en conflit avec un créneau déjà sélectionné");
      return;
    }

    const newSlot: ProposedSlot = {
      id: `custom-${Date.now()}`,
      startTime: slotDate,
      endTime: endTime,
    };

    setProposedSlots((prev) => [...prev, newSlot]);
    setCustomDate("");
    setCustomTime("");
    setShowCustomSlotForm(false);
  };

  // Obtenir la date minimale (aujourd'hui)
  const getMinDate = () => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  };

  // Valider le formulaire - exactement 3 créneaux requis
  const isFormValid = proposedSlots.length === 3;

  // Confirmer l'assignation
  const handleConfirm = async () => {
    // Validation : exactement 3 créneaux requis
    if (proposedSlots.length !== 3) {
      alert("Vous devez proposer exactement 3 créneaux");
      return;
    }

    setIsSubmitting(true);
    try {
      await onAssign(
        proposedSlots.map((slot) => ({
          startTime: slot.startTime,
          endTime: slot.endTime,
        })),
      );
      resetModal();
      onClose();
    } catch (error) {
      console.error("Erreur lors de la soumission:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      sx={{ zIndex: 9999 }}
    >
      {/* Header compact */}
      <div className="relative p-4 bg-[#E3B500] text-white flex justify-between items-center">
        <div className="flex items-center space-x-3">
          <Calendar className="w-5 h-5" />
          <div>
            <h2 className="text-lg font-bold">Proposer 3 créneaux</h2>
            <p className="text-sm text-white/90">
              Exactement 3 créneaux requis
            </p>
          </div>
        </div>
        <IconButton onClick={onClose} className="text-white">
          <X className="w-5 h-5" />
        </IconButton>
      </div>

      {/* Contenu compact */}
      <div className="p-4 max-h-[calc(100vh-200px)] overflow-auto">
        <div className="space-y-4">
          {/* Créneaux proposés */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-[#E3B500]" />
                <h3 className="text-lg font-semibold text-gray-800">
                  Créneaux proposés
                </h3>
              </div>
              <div
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  proposedSlots.length === 3
                    ? "bg-green-100 text-green-800"
                    : "bg-yellow-100 text-yellow-800"
                }`}
              >
                {proposedSlots.length}/3{" "}
                {proposedSlots.length === 3 ? "✓" : "⚠️"}
              </div>
            </div>

            <div className="space-y-2">
              {proposedSlots.length === 0 ? (
                <div className="text-center py-4 text-gray-500">
                  <Calendar className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                  <p className="text-sm">Aucun créneau créé</p>
                  <p className="text-xs text-gray-400">
                    Créez exactement 3 créneaux personnalisés
                  </p>
                </div>
              ) : (
                <div className="space-y-2 sm:space-y-3">
                  {proposedSlots.map((slot, index) => {
                    const formatted = formatDateTime(
                      slot.startTime.toISOString(),
                    );
                    const isCustomSlot = slot.id.startsWith("custom-");
                    return (
                      <div
                        key={slot.id}
                        className={`p-3 sm:p-4 border rounded-lg ${
                          isCustomSlot
                            ? "bg-green-50 border-green-200"
                            : "bg-blue-50 border-blue-200"
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                            <div
                              className={`w-6 h-6 sm:w-8 sm:h-8 text-white rounded-full flex items-center justify-center text-xs sm:text-sm font-medium flex-shrink-0 ${
                                isCustomSlot ? "bg-green-500" : "bg-blue-500"
                              }`}
                            >
                              {index + 1}
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex flex-wrap items-center gap-1 sm:gap-2">
                                <p className="font-medium text-gray-900 text-xs sm:text-sm md:text-base">
                                  {formatted.date}
                                </p>
                                {isCustomSlot && (
                                  <span className="text-xs bg-green-100 text-green-800 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded">
                                    Personnalisé
                                  </span>
                                )}
                              </div>
                              <p className="text-xs sm:text-sm text-gray-600">
                                {formatted.time}
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={() => removeProposedSlot(slot.id)}
                            className="text-red-500 hover:text-red-700 p-1 flex-shrink-0 ml-2"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Créer un créneau personnalisé */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <CalendarDays className="w-5 h-5 text-[#E3B500]" />
                <h3 className="text-lg font-semibold text-gray-800">
                  Ajouter un créneau
                </h3>
              </div>
              <button
                onClick={() => setShowCustomSlotForm(!showCustomSlotForm)}
                disabled={proposedSlots.length >= 3}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                  proposedSlots.length >= 3
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-[#E3B500] text-white hover:bg-[#CA8A04]"
                }`}
              >
                <PlusCircle className="w-4 h-4" />
                {showCustomSlotForm ? "Annuler" : "Nouveau"}
              </button>
            </div>

            {showCustomSlotForm && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                      Date
                    </label>
                    <input
                      type="date"
                      value={customDate}
                      onChange={(e) => setCustomDate(e.target.value)}
                      min={getMinDate()}
                      className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                      Heure de début
                    </label>
                    <input
                      type="time"
                      value={customTime}
                      onChange={(e) => setCustomTime(e.target.value)}
                      className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="text-xs sm:text-sm text-gray-600">
                    <p>• Durée : 1 heure</p>
                    <p>• Créneaux restants : {3 - proposedSlots.length}</p>
                  </div>
                  <button
                    onClick={addCustomSlot}
                    disabled={
                      !customDate || !customTime || proposedSlots.length >= 3
                    }
                    className={`w-full sm:w-auto px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm transition-colors ${
                      !customDate || !customTime || proposedSlots.length >= 3
                        ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                        : "bg-blue-600 text-white hover:bg-blue-700"
                    }`}
                  >
                    Ajouter
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Instructions compactes */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-2">
              <Info className="w-4 h-4 text-blue-600" />
              <h4 className="font-medium text-blue-900 text-sm">
                Instructions
              </h4>
            </div>
            <ul className="text-xs text-blue-800 space-y-1 ml-6">
              <li>
                • <strong>Exactement 3 créneaux requis</strong>
              </li>
              <li>• Choisissez date et heure librement</li>
              <li>• L'élève aura 24h pour choisir</li>
              <li>• Séance de 1 heure</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Footer compact */}
      <div className="p-4 bg-gray-50 border-t flex justify-end gap-3">
        <button
          onClick={onClose}
          className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors text-sm"
        >
          Fermer
        </button>
        <button
          onClick={handleConfirm}
          disabled={!isFormValid || isSubmitting}
          className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors text-sm ${
            isFormValid && !isSubmitting
              ? "bg-[#E3B500] text-white hover:bg-[#CA8A04]"
              : "bg-gray-300 text-gray-500 cursor-not-allowed"
          }`}
        >
          <CheckCircle2 className="w-4 h-4" />
          {isSubmitting ? "Envoi..." : "Proposer 3 créneaux"}
        </button>
      </div>
    </Dialog>
  );
};

export default AssignSlotsModal;
